import { Pagination } from "@mui/lab";
import { Box, Card } from "@mui/material";
import {
  DataGrid,
  GridColumnVisibilityModel,
  GridColumns,
  GridFooterContainer,
  GridPagination,
  GridRowIdGetter,
  GridRowParams,
  GridToolbar,
  gridPageCountSelector,
  gridPageSelector,
  useGridApiContext,
  useGridSelector,
} from "@mui/x-data-grid";
import React from "react";
import { useDispatch, useSelector } from "react-redux";
import { AppGridId, GridIdsToSave } from "../../models/app/AppGridModels";
import {
  addGridSetting,
  useAddAppGridSettingMutation,
} from "../../redux/reducer/features/App/AppGridSlice";
import { AppState } from "../../redux/reducer/rootReducer";

interface ServerAppGridProps {
  columns: GridColumns<any>;
  isLoading: boolean;
  data: any[];
  height?: any;
  onSelect?: any;
  selectedRows?: any;
  getRowId?: GridRowIdGetter<any>;
  setPaging: any;
  customPagesize?: number[];
  paging: any;
  totalRecords: number;
  hideFooter?: boolean;
  hidePaginator?: boolean;
  disableSelection?: boolean;
  keepNonExistentRowsSelected?: boolean | undefined;
  hideColumnFilter?: boolean;
  hideCheckBoxes?: boolean;
  hidePaginationText?: boolean;
  hidePageSize?: boolean;
  gridId?: AppGridId | undefined;
  isRowSelectable?: ((params: GridRowParams<any>) => boolean) | undefined;
}
const ServerAppGrid: React.FunctionComponent<ServerAppGridProps> = (props) => {
  const { userId } = useSelector((state: AppState) => state.authReducer);
  const dispatch = useDispatch();
  const [saveSettingMutation, settingMutationResult] =
    useAddAppGridSettingMutation();

  const handleSetVisibilityModel = (model: GridColumnVisibilityModel) => {
    if (props.gridId !== undefined) {
      dispatch(
        addGridSetting({
          setting: { gridId: props.gridId, columnVisibilityModel: model },
        })
      );
      if (GridIdsToSave.includes(props.gridId)) {
        var data = JSON.stringify(model);
        saveSettingMutation({
          data: { userId: userId ?? "", gridId: props.gridId, setting: data },
        });
      }
    }
  };
  function CustomPagination() {
    const apiRef = useGridApiContext();
    const page = useGridSelector(apiRef, gridPageSelector);
    const pageCount = useGridSelector(apiRef, gridPageCountSelector);

    return (
      <>
        <label></label>
        <Pagination
          color="primary"
          count={pageCount}
          page={page + 1}
          onChange={(event, value) => apiRef.current.setPage(value - 1)}
        />
      </>
    );
  }
  function CustomFooter() {
    return (
      <GridFooterContainer sx={{ width: props.hidePaginator ? "auto" : "80%" }}>
        {props.hidePaginator ? <></> : <CustomPagination />}
        <GridPagination />
      </GridFooterContainer>
    );
  }
  const handleGetColumns = (): GridColumns<any> => {
    return props.columns.map((col) =>
      col.sortable ? col : { ...col, sortable: false }
    );
  };
  return (
    <>
      <Card>
        <Box sx={{ height: props.height ?? 620, width: "100%" }}>
          <DataGrid
            className={`${
              props.hidePaginationText ? "hide-pagination-text" : " "
            }`}
            getRowId={props.getRowId}
            rows={props.data}
            columns={handleGetColumns()}
            rowsPerPageOptions={
              props.customPagesize !== undefined &&
              props.customPagesize.length > 0
                ? props.customPagesize
                : props.hidePageSize
                ? [10, 20, 50, 100, 500, 1000]
                : [10, 20, 50, 100]
            }
            pagination
            paginationMode="server"
            onPageChange={(newPage) => {
              props.setPaging({
                ...props.paging,
                pageIndex: newPage,
              });
            }}
            onPageSizeChange={(model) => {
              props.setPaging({
                ...props.paging,
                pageSize: model,
              });
            }}
            filterMode="server"
            page={props.paging.pageIndex}
            selectionModel={props.selectedRows}
            pageSize={props.hidePageSize ? undefined : props.paging.pageSize}
            hideFooter={props.hideFooter ? true : false}
            filterModel={props.paging}
            onFilterModelChange={(model) => {
              const quickFiltersChanged =
                JSON.stringify(props.paging.quickFilterValues) !==
                JSON.stringify(model.quickFilterValues);
              props.setPaging({
                ...props.paging,
                pageIndex: quickFiltersChanged ? 0 : props.paging.pageIndex,
                items: model.items.map((n) => ({
                  columnField: n.columnField,
                  operatorValue: n.operatorValue ?? "",
                  value: n.value,
                })),
                quickFilterValues: model.quickFilterValues ?? [],
              });
            }}
            onSortModelChange={(model) => {
              props.setPaging({
                ...props.paging,
                pageIndex: 0,
                sorting: model.length > 0 ? model[0] : null,
              });
            }}
            rowCount={props.totalRecords}
            checkboxSelection={props.hideCheckBoxes ? false : true}
            disableSelectionOnClick
            loading={props.isLoading}
            onSelectionModelChange={(newSelection: any, detail: any) => {
              props.onSelect && props.onSelect(newSelection);
            }}
            localeText={{
              footerRowSelected: (count) =>
                count !== 1
                  ? `${count.toLocaleString()} rows selected`
                  : `${count.toLocaleString()} row selected`,
            }}
            components={{ Toolbar: GridToolbar, Pagination: CustomFooter }}
            componentsProps={{
              toolbar: {
                showQuickFilter: true,
                quickFilterProps: { debounceMs: 500 },
              },
            }}
            isRowSelectable={props.isRowSelectable}
            keepNonExistentRowsSelected={
              props.keepNonExistentRowsSelected ?? true
            }
            disableColumnFilter={props.hideColumnFilter}
            onColumnVisibilityModelChange={handleSetVisibilityModel}
          />
        </Box>
      </Card>
    </>
  );
};

export default ServerAppGrid;
